import { Box, Typography } from '@mui/material';

import { sumCounts } from './insightMath';

export type DonutSegment = {
  label: string;
  count: number;
  color: string;
  /** Raw value behind the label (e.g. a custom slot's enum value) — opaque to this component, passed through to onSegmentClick. */
  value?: string;
};

export type DonutChartProps = {
  segments: DonutSegment[];
  /** Big number rendered in the ring's center (the response count). */
  centerValue: number | string;
  centerLabel?: string;
  size?: number;
  ariaLabel?: string;
  onSegmentClick?: (segment: DonutSegment) => void;
};

const STROKE = 14;
const GAP = 1.5; // surface gap between adjacent segments (dataviz mark spec)
// A segment with any positive count must stay visible: subtracting the full
// GAP from a short arc can floor it to 0, making a real response invisible in
// the ring while its legend row still shows a non-zero percentage. Below this
// floor we skip the gap rather than let the segment disappear.
const MIN_VISIBLE_ARC = 1.5;
const TRACK_COLOR = '#E2E8F0';

// Lightweight SVG donut — a ring of stroke-dasharray arcs. Avoids pulling in a
// charting dependency for an MVP that only needs one chart shape.
export const DonutChart = ({
  segments,
  centerValue,
  centerLabel,
  size = 120,
  ariaLabel,
  onSegmentClick,
}: DonutChartProps) => {
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = sumCounts(segments);

  let offset = 0;
  const arcs = total > 0 ? segments.filter(segment => segment.count > 0) : [];

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <Box
        component="svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={ariaLabel}
        sx={{ transform: 'rotate(-90deg)' }}
      >
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={TRACK_COLOR} strokeWidth={STROKE} />
        {arcs.map(segment => {
          const length = (segment.count / total) * circumference;
          const dashLength = length > GAP ? length - GAP : Math.max(length, MIN_VISIBLE_ARC);
          const arc = (
            <circle
              key={segment.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={STROKE}
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-offset}
              onClick={onSegmentClick ? () => onSegmentClick(segment) : undefined}
              style={onSegmentClick ? { cursor: 'pointer' } : undefined}
            />
          );
          offset += length;
          return arc;
        })}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          // This overlay spans the full box (not just the visual center hole), so
          // without this it sits on top of the ring in paint order and swallows
          // every click on the arcs before they ever reach the SVG circles.
          pointerEvents: 'none',
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {centerValue}
        </Typography>
        {centerLabel && (
          <Typography variant="caption" color="text.secondary">
            {centerLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
